import ArchiveIcon from '@mui/icons-material/Archive'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import DeleteIcon from '@mui/icons-material/Delete'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import ImageIcon from '@mui/icons-material/Image'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import GetAppIcon from '@mui/icons-material/GetApp'
import DescriptionIcon from '@mui/icons-material/Description'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  Grid,
} from '@mui/material'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { downloadShareableInventoryCsv } from 'core/inventory/downloadShareableInventoryCSV'
import {
  downloadInventoryPdfFromVehicles,
  type PdfColumnConfig,
} from 'core/inventory/downloadShareableInventoryPdf'
import { handleInventoryCsvUpload } from 'core/inventory/handleInventoryCsvUpload'
import { useAdminListBuilder } from 'hooks/useAdminListBuilder'
import { useInventory } from 'hooks/inventory/useInventory'
import { useUsers } from 'hooks/useUsers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { InventoryList } from '@otw/models/inventory'
import { Vehicle } from '@otw/models/inventory'
import { PrivateUserInfoType } from '@otw/models'
import SoldDialog from '@/components/inventory/dialogs/SoldDialog'
import { storedShippingFromDialog } from '@/components/inventory/dialogs/soldShipping'
import PendingSaleNotPurchasedDialog from '@/components/inventory/dialogs/PendingSaleNotPurchasedDialog'
import VehicleSaleSelectionDialog from '@/components/inventory/dialogs/VehicleSaleSelectionDialog'
import BulkPriceUpdateDialog from '@/components/inventory/dialogs/BulkPriceUpdateDialog'
import { usePhotosForInventoryObject } from '@/hooks/inventory/usePhotosForInventoryObject'
import ManagePhotosDialog from '@/components/inventory/dialogs/ManagePhotosDialog'
import { usePermissions } from 'hooks/auth/usePermissions'
import { pandaApi } from 'core/api/modules/pandaApi'
import InvoiceDialog, { SignerInfo, VehicleBankProfileAssignment } from './InvoiceDialog'
import SaleInvoiceDialog, { SaleInvoiceSubmitData } from './SaleInvoiceDialog'
import { saveVehicleInvoice } from '@/hooks/inventory/useDocuments'
import { useAdminDealApproval } from '@/hooks/admin/useAdminDealApproval'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DealApprovalVehicleCard from '@/views/SuperAdmin/DealApproval/components/VehicleCard'
import {
  generateDownPaymentPDF,
  generateSaleInvoicePDF,
  invoiceClientInfoFromRecipient,
  InvoiceCompanyInfo,
} from '@/views/SuperAdmin/Management/LeadManagement/util/pdfGenerators'
import { storage } from '@/config/firebase'
import { ref, uploadBytes, getDownloadURL } from '@firebase/storage'
import { useInvoices } from '@/hooks/admin/useInvoices'
import { useWireInfo } from '@/hooks/admin/useWireInfo'
import { useLeads } from '@/hooks/admin/useLeads'
import DealApprovalWarningDialog from '@/views/SuperAdmin/Management/LeadManagement/components/dialogs/DealApprovalWarningDialog'
import { getPendingSaleLeadRequirementError } from '@/views/SuperAdmin/Management/LeadManagement/util/leadDropoffValidation'
import { vehiclesNeedingPendingSaleNotPurchasedGate } from '@/components/inventory/utils/pendingSaleNotPurchasedGate'
import { BankProfile } from '@otw/models'
import { getVehicleDecisionStatus } from '@otw/models/deals'

interface AddToListDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (listId: string | 'new', listName?: string, description?: string, associatedUserIds?: string[]) => void
  lists: InventoryList[]
  loading: boolean
  users: PrivateUserInfoType[]
}

const AddToListDialog: React.FC<AddToListDialogProps> = ({ open, onClose, onSubmit, lists, loading, users }) => {
  const [selectedList, setSelectedList] = useState<string>('new')
  const [newListName, setNewListName] = useState('')
  const [newListDescription, setNewListDescription] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<PrivateUserInfoType[]>([])

  useEffect(() => {
    if (open) {
      setSelectedList('new')
      setNewListName('')
      setNewListDescription('')
      setSelectedUsers([])
    }
  }, [open])

  useEffect(() => {
    if (selectedList !== 'new' && lists) {
      const currentList = lists.find(list => list.id === selectedList)
      const userIds = currentList?.associatedUserIds || []
      const associatedUsers = users.filter(user => userIds.includes(user.id))
      setSelectedUsers(associatedUsers)
    }
  }, [selectedList, lists, users])

  const handleSubmit = () => {
    if (selectedList === 'new' && !newListName) return
    onSubmit(
      selectedList,
      newListName,
      newListDescription,
      selectedUsers.map(user => user.id),
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add to List</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Select List</InputLabel>
            <Select value={selectedList} onChange={e => setSelectedList(e.target.value)} label='Select List'>
              <MenuItem value='new'>Create New List</MenuItem>
              {lists.map(list => (
                <MenuItem key={list.id} value={list.id}>
                  {list.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedList === 'new' && (
            <>
              <TextField
                label='List Name'
                value={newListName}
                onChange={e => setNewListName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label='Description'
                value={newListDescription}
                onChange={e => setNewListDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </>
          )}

          <Autocomplete
            multiple
            options={users}
            value={selectedUsers}
            onChange={(_, newValue) => setSelectedUsers(newValue)}
            getOptionLabel={option =>
              `${option.email || ''}${option.company_name ? ` (${option.company_name})` : ''}`
            }
            renderInput={params => <TextField {...params} label='Share with Users' placeholder='Search users...' />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip label={option.email} {...getTagProps({ index })} key={option.id} />)
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Stack>
                  <Typography>{option.email || 'No email'}</Typography>
                  {option.company_name && (
                    <Typography variant='caption' color='text.secondary'>
                      {option.company_name}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            filterOptions={(options, { inputValue }) => {
              const searchTerms = inputValue.toLowerCase().split(' ')
              return options.filter(option =>
                searchTerms.every(
                  term =>
                    (option.email?.toLowerCase() || '').includes(term) ||
                    (option.company_name?.toLowerCase() || '').includes(term) ||
                    (option.first_name?.toLowerCase() || '').includes(term) ||
                    (option.last_name?.toLowerCase() || '').includes(term),
                ),
              )
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={(selectedList === 'new' && !newListName) || loading}
          variant='contained'
        >
          {loading ? 'Adding...' : 'Add to List'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const LoadingDialog = ({ open, message }: { open: boolean; message: string }) => (
  <Dialog open={open} PaperProps={{ sx: { backgroundColor: 'background.paper', padding: 2 } }}>
    <DialogContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>{message}</Typography>
      </Box>
    </DialogContent>
  </Dialog>
)

const DeleteConfirmationDialog = ({
  open,
  vehicles,
  onConfirm,
  onCancel,
}: {
  open: boolean
  vehicles: Vehicle[]
  onConfirm: () => void
  onCancel: () => void
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth>
    <DialogTitle sx={{ color: 'error.main' }}>Confirm Delete</DialogTitle>
    <DialogContent>
      <Typography gutterBottom>Are you sure you want to delete the following vehicles?</Typography>
      <List>
        {vehicles.map(vehicle => (
          <ListItem key={vehicle.id} divider>
            <ListItemText
              primary={`${vehicle.stock_number} - ${vehicle.make} ${vehicle.model}`}
              secondary={`Added: ${vehicle.date_added ? new Date(vehicle.date_added).toLocaleDateString('en-US') : 'N/A'}`}
            />
          </ListItem>
        ))}
      </List>
      <Typography color='error' sx={{ mt: 2 }}>
        This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button onClick={onConfirm} color='error' variant='contained'>
        Delete
      </Button>
    </DialogActions>
  </Dialog>
)

const DealApprovalConfirmationDialog = ({
  open,
  vehicles,
  onConfirm,
  onCancel,
  loading,
  inventoryType,
  canViewPurchasePrice,
  canViewProfit,
}: {
  open: boolean
  vehicles: Vehicle[]
  onConfirm: () => void
  onCancel: () => void
  loading: boolean
  inventoryType: 'master_inventory' | 'shield_inventory'
  canViewPurchasePrice: boolean
  canViewProfit: boolean
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth='lg' fullWidth>
    <DialogTitle>Create Deal Approval Request</DialogTitle>
    <DialogContent>
      <Typography gutterBottom>
        You are about to create a deal approval request for the following {vehicles.length} vehicle(s):
      </Typography>
      <Box sx={{ maxHeight: 500, overflow: 'auto', mt: 2 }}>
        <Grid container spacing={2}>
          {vehicles.map(vehicle => (
            <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
              <DealApprovalVehicleCard
                collectionName={inventoryType}
                vehicle={vehicle}
                canViewPurchasePrice={canViewPurchasePrice}
                canViewProfit={canViewProfit}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
        This will create a pending deal approval request that can be reviewed and approved by deal approvers.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant='contained'
        disabled={loading}
        startIcon={loading ? undefined : <CheckCircleIcon />}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            Creating...
          </Box>
        ) : (
          'Get Approved'
        )}
      </Button>
    </DialogActions>
  </Dialog>
)

const ActionsPanel = React.memo(
  ({
    selectedRows,
    clearAllFilters,
    getVisibleColumnsForPdf,
  }: {
    selectedRows: Vehicle[]
    clearAllFilters: () => void
    getVisibleColumnsForPdf?: () => PdfColumnConfig[]
  }) => {
    const RETAIL_ORDER_TEMPLATE_NAME = 'Retail Order'
    const { userInfo } = useAuth()
    const { checkPermission, isPermissionsLoading } = usePermissions()
    const [loading, setLoading] = useState(false)
    const [loadingMessage, setLoadingMessage] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const theme = useTheme()
    const location = useLocation()
    const inventoryType = location.pathname.includes('shield-inventory')
      ? 'shield_inventory'
      : 'master_inventory'
    const { deleteDocument, updateDocument, updateDocumentAsync, vehicles } = useInventory(inventoryType, undefined, {
      dontShowUpdates: true,
    })
    const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
    const [showAddToList, setShowAddToList] = useState(false)
    const {
      lists,
      loading: listsLoading,
      createList,
      addVehiclesToList,
      updateListAssociations,
    } = useAdminListBuilder()
    const { users } = useUsers()
    const { createDealApproval, isCreating: isCreatingDeal, deals: allDeals, isLoading: isLoadingDeals } = useAdminDealApproval({
      dontShowUpdates: true,
    })
    const { generateInvoiceNumber } = useInvoices()
    const { wireInfo, isLoading: isLoadingWireInfo } = useWireInfo()
    const { leads } = useLeads()

    // Permission checks
    const canModifyInventory = checkPermission('inventory', 'modify')
    const canCreateInventory = checkPermission('inventory', 'create')
    const canDeleteInventory = checkPermission('inventory', 'delete')
    const canMarkSold = checkPermission('inventory', 'mark_sold')
    const canMarkSoldElsewhere = checkPermission('inventory', 'mark_sold_elsewhere')
    const canMarkPendingSaleNotPurchased = checkPermission(
      'inventory',
      'mark_pending_sale_not_purchased',
    )
    const canViewPurchasePrice = checkPermission('inventory', 'view_purchase_price')
    const canViewProfit = checkPermission('inventory', 'view_profit')
    const canBulkUpdatePrice = checkPermission('inventory', 'bulk_update_price')

    // Combined loading state including permissions loading
    const isLoading = loading || isPermissionsLoading

    // Add states for the dialogs
    const [vehicleSelectionOpen, setVehicleSelectionOpen] = useState(false)
    const [soldPopupOpen, setSoldPopupOpen] = useState(false)
    const [saleActionType, setSaleActionType] = useState<'sold' | 'pending_sale'>('sold')
    const [selectedVehiclesToSell, setSelectedVehiclesToSell] = useState<Vehicle[]>([])
    const [remainingVehicles, setRemainingVehicles] = useState<Vehicle[]>([])

    // Add state for pending updates
    const [pendingUpdates, setPendingUpdates] = useState<Array<{ id: string; data: any }>>([])
    const [managePhotosOpen, setManagePhotosOpen] = useState(false)
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
    const [dealApprovalDialogOpen, setDealApprovalDialogOpen] = useState(false)
    const [saleInvoiceDialogOpen, setSaleInvoiceDialogOpen] = useState(false)
    const [saleInvoiceDealApprovalDialogOpen, setSaleInvoiceDealApprovalDialogOpen] = useState(false)
    const [isGeneratingSaleInvoice, setIsGeneratingSaleInvoice] = useState(false)
    const [bulkPriceUpdateOpen, setBulkPriceUpdateOpen] = useState(false)
    const [pendingSaleNotPurchasedOpen, setPendingSaleNotPurchasedOpen] = useState(false)
    const [pendingSaleNotPurchasedVehicles, setPendingSaleNotPurchasedVehicles] = useState<
      Vehicle[]
    >([])

    // Determine the correct button text and action based on permissions
    const getSoldButtonText = () => {
      if (canMarkSold && canMarkSoldElsewhere) return 'Mark Sold'
      if (canMarkSold) return 'Mark Sold'
      if (canMarkSoldElsewhere) return 'Mark Sold Elsewhere'
      return 'Mark Sold' // Default text
    }

    const getSoldButtonTooltip = () => {
      if (!canMarkSold && !canMarkSoldElsewhere) {
        return "You don't have permission to mark items as sold"
      }
      if (!canMarkSold && canMarkSoldElsewhere) {
        return 'You can only mark items as Sold Elsewhere (in the grid view)'
      }
      return 'Mark all selected vehicles as sold (in the grid view)'
    }

    const handleDownloadCsv = useCallback(async () => {
      try {
        setLoadingMessage('Downloading CSV...')
        setLoading(true)
        const selectedIds = selectedRows.map(row => row.id)
        if (!userInfo?.role) {
          showError('Unknown error occurred..please contact admin@optictruckworks.com')
          return
        }
        await downloadShareableInventoryCsv(
          inventoryType, 
          selectedIds, 
          checkPermission('inventory', 'view_purchase_price'),
          checkPermission('inventory', 'view_profit')
        )
      } finally {
        setLoading(false)
      }
    }, [selectedRows, userInfo?.role, inventoryType, showSnackbar, checkPermission])

    const pdfColumnCount = getVisibleColumnsForPdf?.()?.length ?? 0
    const pdfColumnLimitExceeded = pdfColumnCount > 15

    const handleDownloadPdf = useCallback(() => {
      try {
        setLoadingMessage('Downloading PDF...')
        setLoading(true)
        if (selectedRows.length === 0) {
          showError('Select at least one vehicle to download a PDF')
          return
        }
        downloadInventoryPdfFromVehicles(
          inventoryType,
          selectedRows as unknown as Array<Record<string, unknown>>,
        )
        showSuccess('PDF downloaded')
      } catch (error) {
        console.error('Inventory PDF download failed:', error)
        showError('Could not download PDF. Please try again.')
      } finally {
        setLoading(false)
      }
    }, [selectedRows, inventoryType, showError, showSuccess])

    const handleDownloadCustomPdf = useCallback(() => {
      try {
        setLoadingMessage('Downloading PDF...')
        setLoading(true)
        if (selectedRows.length === 0) {
          showError('Select at least one vehicle to download a PDF')
          return
        }
        const columnConfig = getVisibleColumnsForPdf?.()?.length
          ? getVisibleColumnsForPdf()
          : undefined
        downloadInventoryPdfFromVehicles(
          inventoryType,
          selectedRows as unknown as Array<Record<string, unknown>>,
          columnConfig,
        )
        showSuccess('PDF downloaded')
      } catch (error) {
        console.error('Inventory custom PDF download failed:', error)
        showError('Could not download PDF. Please try again.')
      } finally {
        setLoading(false)
      }
    }, [selectedRows, inventoryType, getVisibleColumnsForPdf, showError, showSuccess])

    const handleDelete = useCallback(async () => {
      try {
        setLoadingMessage('Deleting...')
        setLoading(true)
        await Promise.all(selectedRows.map(row => deleteDocument(row.id)))
      } finally {
        setLoading(false)
        setShowDeleteConfirm(false)
      }
    }, [selectedRows, deleteDocument])

    // Common sizing for all buttons
    const buttonSizing = {
      fontSize: '0.8rem',
      minHeight: '36px',
      padding: '6px 16px',
      width: '100%',
    }
    const tooltipWrapStyle = { display: 'block', width: '100%' } as const

    const buttonStyles = {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(69, 85, 115, 0.3)' : 'rgba(69, 85, 115, 0.12)',
        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(69, 85, 115, 0.26)',
      },
      ...buttonSizing,
    }

    const deleteButtonStyles = {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.error.dark : theme.palette.error.main,
      color: theme.palette.error.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.error.main : theme.palette.error.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.12)',
        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(244, 67, 54, 0.26)',
      },
      ...buttonSizing,
    }

    const customPdfButtonStyles = {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.warning.main : theme.palette.warning.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.3)' : 'rgba(237, 108, 2, 0.12)',
        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(237, 108, 2, 0.26)',
      },
      ...buttonSizing,
    }

    // Start the process
    const handleMarkSold = useCallback(() => {
      // Check permissions first
      if (!canMarkSold && !canMarkSoldElsewhere) {
        showError('You do not have permission to mark vehicles as sold')
        return
      }

      if (!canMarkSold && canMarkSoldElsewhere) {
        showWarning('You can only mark vehicles as "Sold Elsewhere"')
        // Could continue with a modified flow specifically for Sold Elsewhere
      }

      setSaleActionType('sold')
      // Initialize with all selected vehicles
      setRemainingVehicles([...selectedRows])
      setVehicleSelectionOpen(true)
      setSoldPopupOpen(false)
      // Clear any pending updates
      setPendingUpdates([])
    }, [selectedRows, canMarkSold, canMarkSoldElsewhere, showSnackbar])

    const beginPendingSaleFlow = useCallback(() => {
      setSaleActionType('pending_sale')
      setRemainingVehicles([...selectedRows])
      setVehicleSelectionOpen(true)
      setSoldPopupOpen(false)
      setPendingUpdates([])
    }, [selectedRows])

    const handleMarkPendingSale = useCallback(() => {
      if (!canMarkSold) {
        showError('You do not have permission to mark vehicles as pending sale')
        return
      }

      const failingVehicles = selectedRows.filter(
        vehicle => getPendingSaleLeadRequirementError(leads, vehicle.id) !== null,
      )
      if (failingVehicles.length > 0) {
        const labels = failingVehicles
          .map(vehicle => vehicle.stock_number || vehicle.id)
          .join(', ')
        showError(
          `Cannot mark Pending Sale. Each truck must be linked to a lead with a dropoff location selected. Missing requirements for: ${labels}`,
        )
        return
      }

      const gatedVehicles = vehiclesNeedingPendingSaleNotPurchasedGate(
        selectedRows,
        canMarkPendingSaleNotPurchased,
      )
      if (gatedVehicles.length > 0) {
        setPendingSaleNotPurchasedVehicles(gatedVehicles as Vehicle[])
        setPendingSaleNotPurchasedOpen(true)
        return
      }

      beginPendingSaleFlow()
    }, [
      selectedRows,
      canMarkSold,
      canMarkPendingSaleNotPurchased,
      showError,
      leads,
      beginPendingSaleFlow,
    ])

    const handleNotifyPendingSaleNotPurchased = useCallback(
      async (
        vehiclesToNotify: Array<
          Pick<Vehicle, 'id' | 'stock_number' | 'pending_sale_not_purchased_notify'>
        >,
      ) => {
        for (const vehicle of vehiclesToNotify) {
          if (vehicle.pending_sale_not_purchased_notify === true) continue
          await updateDocumentAsync({
            id: vehicle.id,
            data: { pending_sale_not_purchased_notify: true },
          })
        }
      },
      [updateDocumentAsync],
    )

    const handleOpenBulkPriceUpdate = useCallback(() => {
      if (!canBulkUpdatePrice) {
        showError('You do not have permission to bulk update prices')
        return
      }
      if (selectedRows.length === 0) {
        showWarning('Please select at least one vehicle')
        return
      }
      setBulkPriceUpdateOpen(true)
    }, [selectedRows, canBulkUpdatePrice, showError, showWarning])

    const handleBulkPriceUpdate = useCallback(
      async (updates: Array<{ id: string; optic_list_price: number }>) => {
        if (!canBulkUpdatePrice) {
          showError('You do not have permission to bulk update prices')
          return
        }
        if (updates.length === 0) {
          showWarning('No price changes to save')
          return
        }

        try {
          setLoadingMessage('Updating optic list prices...')
          setLoading(true)
          await Promise.all(
            updates.map(update =>
              updateDocument({
                id: update.id,
                data: { optic_list_price: update.optic_list_price },
              }),
            ),
          )
          showSuccess(`Updated optic list price for ${updates.length} vehicle(s)`)
          setBulkPriceUpdateOpen(false)
        } catch (error) {
          console.error('Error updating optic list prices:', error)
          showError('Failed to update optic list prices')
        } finally {
          setLoading(false)
        }
      },
      [updateDocument, canBulkUpdatePrice, showWarning, showSuccess, showError],
    )

    // Mark selected vehicles as funded (funded=true, funded_date=now). Only vehicles with purchased_for_inventory=true are eligible.
    const handleMarkFunded = useCallback(async () => {
      if (selectedRows.length === 0) {
        showWarning('Please select at least one vehicle')
        return
      }
      const fundedEligibleRows = selectedRows.filter(v => v.purchased_for_inventory === true)
      if (fundedEligibleRows.length === 0) {
        showWarning('Select only vehicles with "Purchased for inventory" to mark as funded')
        return
      }
      const currentDate = new Date().toISOString()
      const updateData = { funded: true, funded_date: currentDate }
      try {
        setLoadingMessage('Marking vehicles as funded...')
        setLoading(true)
        await Promise.all(
          fundedEligibleRows.map(row =>
            updateDocument({
              id: row.id,
              data: updateData,
            })
          )
        )
        showSuccess(`Marked ${fundedEligibleRows.length} vehicle(s) as funded`)
      } catch (error) {
        console.error('Error marking vehicles as funded:', error)
        showError('Failed to mark vehicles as funded')
      } finally {
        setLoading(false)
      }
    }, [selectedRows, updateDocument, showWarning, showSuccess, showError])

    // Handle vehicle selection confirmation
    const handleVehicleSelectionConfirm = useCallback((selectedVehicles: Vehicle[]) => {
      // Only proceed if vehicles were selected
      if (selectedVehicles.length > 0) {
        setSelectedVehiclesToSell(selectedVehicles)
        setSoldPopupOpen(true)
      } else {
        // No vehicles selected, close the dialog
        setVehicleSelectionOpen(false)
      }
    }, [])

    // Add new handler for sold dialog submit
    const handleSoldSubmit = useCallback(
      async (
        soldToUser: { id: string; name: string },
        soldByUser: { id: string; name: string },
        sold_location: string,
        warrantyInfo?: {
          warrantyTerms: string | null
          warrantyCost: number | null
          customerCost: number | null
          warrantyProfit: number | null
        } | null,
        financeInfo?: {
          paymentType: string
          downPayment: number | null
          bankName: string | null
          amountFinanced: number | null
          points: number | null
          profit: number | null
          totalSalePrice: number | null
          taxRate: number | null
          includeTaxInSale: boolean
          includeWarrantyInSale: boolean
          includeShippingInSale: boolean
          transleaseLease?: boolean
        } | null,
        shippingInfo?: {
          shipped: boolean | null
          ourShippingCost: number | null
          customerShippingCost: number | null
          profit: number | null
        } | null,
        funded?: boolean,
        soldToContactId?: string | null,
      ) => {
        try {
          // Don't set loading state yet - we're just queueing updates

          // Create a date set to noon today to avoid timezone issues
          const today = new Date()
          today.setHours(12, 0, 0, 0) // Set to noon local time
          const currentDate = today.toISOString()

          // Ensure sold_to_user and sold_by_user are valid objects
          if (!soldToUser?.id || !soldToUser?.name) {
            showError('Error: Invalid sold to user data')
            console.error('Invalid soldToUser data:', soldToUser)
            return
          }

          if (!soldByUser?.id || !soldByUser?.name) {
            showError('Error: Invalid sold by user data')
            console.error('Invalid soldByUser data:', soldByUser)
            return
          }

          const contactId = soldToContactId?.trim() || null

          if (saleActionType === 'pending_sale') {
            const vehiclesToValidate = [
              ...selectedVehiclesToSell,
              ...remainingVehicles.filter(
                remaining => !selectedVehiclesToSell.some(selected => selected.id === remaining.id),
              ),
            ]
            const failingVehicles = vehiclesToValidate.filter(
              vehicle => getPendingSaleLeadRequirementError(leads, vehicle.id) !== null,
            )
            if (failingVehicles.length > 0) {
              const labels = failingVehicles
                .map(vehicle => vehicle.stock_number || vehicle.id)
                .join(', ')
              showError(
                `Cannot mark Pending Sale. Each truck must be linked to a lead with a dropoff location selected. Missing requirements for: ${labels}`,
              )
              return
            }
          }

          // Determine the correct status based on permissions and action type
          const status =
            saleActionType === 'pending_sale'
              ? 'Pending Sale'
              : canMarkSold
                ? 'Sold'
                : canMarkSoldElsewhere
                  ? 'Sold Elsewhere'
                  : 'Sold'

          // Show warning if using Sold Elsewhere due to permissions
          if (saleActionType === 'sold' && !canMarkSold && canMarkSoldElsewhere) {
            showInfo('Vehicle(s) will be marked as "Sold Elsewhere" based on your permissions')
          }

          // Create the update data object explicitly
          const updateData: any =
            saleActionType === 'pending_sale'
              ? {
                  status: 'Pending Sale',
                  selling_to_user: {
                    id: soldToUser.id,
                    name: soldToUser.name,
                  },
                  selling_to_contact_id: contactId,
                  sold_by_user: {
                    id: soldByUser.id,
                    name: soldByUser.name,
                  },
                  sold_location: sold_location,
                }
              : {
                  status: status,
                  sold_to_user: {
                    id: soldToUser.id,
                    name: soldToUser.name,
                  },
                  sold_to_contact_id: contactId,
                  sold_by_user: {
                    id: soldByUser.id,
                    name: soldByUser.name,
                  },
                  sold_location: sold_location,
                  sold_date: currentDate,
                  show_on_website: false,
                }

          if (saleActionType === 'sold') {
            if (warrantyInfo) {
              updateData['warranty_purchaged'] = true
              updateData['warranty'] = {
                warranty_terms: warrantyInfo?.warrantyTerms,
                warranty_cost: warrantyInfo?.warrantyCost,
                customer_cost: warrantyInfo?.customerCost,
                warranty_created_at: new Date().toISOString(),
                warranty_profit: warrantyInfo?.warrantyProfit,
              }
            }

            // Add payment type and finance information if provided
            if (financeInfo) {
              // Create the finance object with payment type included
              updateData['finance'] = {
                payment_type: financeInfo.paymentType,
                down_payment: financeInfo.downPayment !== null ? Number(financeInfo.downPayment) : null,
                bank_name: financeInfo.bankName,
                amount_financed: financeInfo.amountFinanced !== null ? Number(financeInfo.amountFinanced) : null,
                points: financeInfo.points !== null ? Number(financeInfo.points) : null,
                profit: financeInfo.profit !== null ? Number(financeInfo.profit) : null,
                total_sale_price: financeInfo.totalSalePrice !== null ? Number(financeInfo.totalSalePrice) : null,
                tax_rate: financeInfo.taxRate !== null ? Number(financeInfo.taxRate) : null,
                include_tax_in_sale: financeInfo.includeTaxInSale,
                include_warranty_in_sale: financeInfo.includeWarrantyInSale,
                include_shipping_in_sale: financeInfo.includeShippingInSale,
              }
              updateData['translease_lease'] = Boolean(financeInfo.transleaseLease)
            }

            const storedShipping = storedShippingFromDialog(shippingInfo)
            if (storedShipping) {
              updateData['shipping'] = storedShipping
            }
          }

          // Add updates to the pending list instead of executing them immediately
          // funded / funded_date are merged per row so an existing funded date is preserved when marking sold
          const updates = selectedVehiclesToSell.map(row => {
            const data = { ...updateData }
            if (saleActionType === 'sold' && funded !== undefined && status === 'Sold') {
              if (funded) {
                data.funded = true
                data.funded_date = row.funded_date ?? currentDate
              } else {
                data.funded = false
                data.funded_date = null
              }
            }
            return { id: row.id, data }
          })

          setPendingUpdates(prev => [...prev, ...updates])

          // Update remaining vehicles by removing the ones we just processed
          const selectedIds = new Set(selectedVehiclesToSell.map(v => v.id))
          const updatedRemainingVehicles = remainingVehicles.filter(v => !selectedIds.has(v.id))

          // Reset selected vehicles for the next batch
          setSelectedVehiclesToSell([])
          setSoldPopupOpen(false)

          // If there are no more vehicles to process, execute all pending updates
          if (updatedRemainingVehicles.length === 0) {
            // Show loading while we process all the updates
            setLoadingMessage(
              saleActionType === 'pending_sale'
                ? 'Marking vehicles as Pending Sale...'
                : `Marking vehicles as ${status}...`,
            )
            setLoading(true)

            try {
              // Execute all pending updates at once
              await Promise.all(
                pendingUpdates.concat(updates).map(update =>
                  updateDocument({
                    id: update.id,
                    data: update.data,
                  }),
                ),
              )

              showSuccess(
                saleActionType === 'pending_sale'
                  ? 'Successfully marked all vehicles as Pending Sale'
                  : `Successfully marked all vehicles as ${status}`,
              )

              // Close all dialogs
              setVehicleSelectionOpen(false)

              // Clear pending updates
              setPendingUpdates([])
              setRemainingVehicles([])
            } catch (error) {
              showError(
                saleActionType === 'pending_sale'
                  ? 'Failed to mark vehicles as Pending Sale'
                  : `Failed to mark vehicles as ${status}`,
              )
              console.error('Error marking vehicles as sold:', error)
            } finally {
              setLoading(false)
            }
          } else {
            // Go back to selection step with remaining vehicles
            setRemainingVehicles(updatedRemainingVehicles)
            setVehicleSelectionOpen(true)
          }
        } catch (error) {
          showError('Failed to prepare updates')
          console.error('Error preparing updates:', error)
        }
      },
      [
        selectedVehiclesToSell,
        remainingVehicles,
        pendingUpdates,
        updateDocument,
        showError,
        saleActionType,
        canMarkSold,
        canMarkSoldElsewhere,
        vehicles,
        leads,
      ],
    )

    const handleAddToList = useCallback(
      async (listId: string | 'new', listName?: string, description?: string, associatedUserIds?: string[]) => {
        try {
          setLoadingMessage('Adding to list...')
          setLoading(true)

          const selectedIds = selectedRows.map(row => row.id)

          if (listId === 'new' && listName) {
            await createList({
              name: listName,
              description: description || '',
              initialInventoryIds: selectedIds,
              associatedUserIds: associatedUserIds || [],
            })
          } else if (listId !== 'new') {
            // Add to existing list
            await addVehiclesToList({
              listId,
              vehicleIds: selectedIds,
            })
            // Update user associations if provided
            if (associatedUserIds) {
              await updateListAssociations({
                listId,
                userIds: associatedUserIds,
              })
            }
          }

          showSuccess('Vehicles added to list successfully')
          setShowAddToList(false)
        } catch (error) {
          console.error('Error adding to list:', error)
          showError('Failed to add vehicles to list')
        } finally {
          setLoading(false)
        }
      },
      [selectedRows, createList, addVehiclesToList, updateListAssociations, showSnackbar],
    )

    // Handler for opening the manage photos dialog
    const handleManagePhotos = useCallback(() => {
      if (selectedRows.length === 1) {
        setManagePhotosOpen(true)
      } else {
        showWarning('Please select exactly one vehicle to manage photos')
      }
    }, [selectedRows, showSnackbar])

    // Handler for downloading CSV template
    const handleDownloadCSVTemplate = useCallback(() => {
      window.open('/inventory_template.csv')
    }, [])

    // Handler for CSV upload
    const handleCsvUpload = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleInventoryCsvUpload(event, inventoryType)
      },
      [inventoryType],
    )

    // Handler for opening invoice dialog
    const handleOpenInvoiceDialog = useCallback(() => {
      if (selectedRows.length === 0) {
        showWarning('Please select at least one vehicle to generate an invoice')
        return
      }
      setInvoiceDialogOpen(true)
    }, [selectedRows, showSnackbar])

    // Handler for opening deal approval dialog
    const handleOpenDealApprovalDialog = useCallback(() => {
      if (selectedRows.length === 0) {
        showWarning('Please select at least one vehicle to create a deal approval')
        return
      }
      setDealApprovalDialogOpen(true)
    }, [selectedRows, showSnackbar])

    // Handler for confirming deal approval creation
    const handleConfirmDealApproval = useCallback(() => {
      if (selectedRows.length === 0) {
        showError('No vehicles selected')
        return
      }

      const vehicleIds = selectedRows.map(row => row.id)
      const vehicle_summaries = selectedRows.map(row => ({
        stock_number: row.stock_number ?? '',
        vin: row.vin ?? '',
      }))
      createDealApproval(
        {
          vehicle_ids: vehicleIds,
          vehicle_summaries,
        },
        {
          onSuccess: () => {
            showSuccess(`Deal approval request created for ${vehicleIds.length} vehicle(s)`)
            setDealApprovalDialogOpen(false)
          },
          onError: (error: Error) => {
            console.error('Error creating deal approval:', error)
            showError('Failed to create deal approval request')
          },
        }
      )
    }, [selectedRows, createDealApproval, showSnackbar])

    // Vehicles eligible for "Mark as funded" (purchased_for_inventory=true only)
    const fundedEligibleRows = selectedRows.filter(v => v.purchased_for_inventory === true)

    // --- Sale Invoice (Retail Order) for Pending Sale vehicles ---
    const PENDING_SALE = 'Pending Sale'
    const pendingSaleRows = selectedRows.filter(
      v => v.status === PENDING_SALE && v.selling_to_user?.id && v.selling_to_user?.name
    )
    const firstBuyer = pendingSaleRows[0]?.selling_to_user ?? null
    const allSameBuyer =
      firstBuyer &&
      pendingSaleRows.length > 0 &&
      pendingSaleRows.every(v => v.selling_to_user?.id === firstBuyer.id)
    const buyerUser = firstBuyer ? users.find(u => u.id === firstBuyer.id) : null
    const canGenerateSaleInvoice =
      pendingSaleRows.length > 0 &&
      selectedRows.length === pendingSaleRows.length &&
      !!allSameBuyer &&
      !!buyerUser?.email
    const saleInvoiceInitialRecipient = useMemo(() => {
      if (!firstBuyer || !buyerUser?.email) {
        return undefined
      }

      const buyerName = firstBuyer.name
      const nameParts = buyerName.split(' ')
      return {
        email: buyerUser.email,
        first_name: buyerUser.first_name || nameParts[0] || buyerName,
        last_name: buyerUser.last_name || nameParts.slice(1).join(' '),
        company: buyerUser.company_name || '',
        street_address: buyerUser.address?.street || '',
        city: buyerUser.address?.city || '',
        state: buyerUser.address?.state || '',
        postal_code: buyerUser.address?.zipCode || '',
      }
    }, [firstBuyer, buyerUser])

    const getInvoiceCompanyInfo = useCallback((): InvoiceCompanyInfo | null => {
      if (!wireInfo) return null
      return {
        company: wireInfo.company || 'not found',
        address: wireInfo.address || 'not found',
        bank: wireInfo.bank || 'not found',
        routingNumber: wireInfo.routingNumber || 'not found',
        accountNumber: wireInfo.accountNumber || 'not found',
        phone: undefined,
      }
    }, [wireInfo])

    const checkAllSaleInvoiceVehiclesInApprovedDeal = useCallback((): boolean => {
      if (pendingSaleRows.length === 0) return true
      if (isLoadingDeals) return false
      const vehicleIds = pendingSaleRows.map(v => v.id)
      for (const vehicleId of vehicleIds) {
        const isApproved = allDeals.some(deal => {
          if (!deal.vehicle_ids.includes(vehicleId)) return false
          const status = getVehicleDecisionStatus(deal.vehicle_decisions?.[vehicleId])
          return status === 'approved' || status === 'conditionally_approved'
        })
        if (!isApproved) return false
      }
      return true
    }, [pendingSaleRows, allDeals, isLoadingDeals])

    const findExistingDealForSaleInvoice = useCallback(() => {
      if (pendingSaleRows.length === 0 || isLoadingDeals) return null
      const ids = new Set(pendingSaleRows.map(v => v.id))
      const existing = allDeals.find(deal => {
        const dealIds = new Set(deal.vehicle_ids)
        return dealIds.size === ids.size && Array.from(ids).every(id => dealIds.has(id))
      })
      return existing ?? null
    }, [pendingSaleRows, allDeals, isLoadingDeals])

    const handleOpenSaleInvoiceDialog = useCallback(() => {
      if (pendingSaleRows.length === 0) {
        showWarning('Select only vehicles with status "Pending Sale" that have a buyer')
        return
      }
      if (!allSameBuyer || !firstBuyer) {
        showWarning('All selected vehicles must have the same buyer to generate a sale invoice')
        return
      }
      if (!buyerUser?.email) {
        showWarning('Buyer must exist in users with an email to generate sale invoice')
        return
      }
      if (!checkAllSaleInvoiceVehiclesInApprovedDeal()) {
        setSaleInvoiceDealApprovalDialogOpen(true)
      } else {
        setSaleInvoiceDialogOpen(true)
      }
    }, [
      pendingSaleRows,
      allSameBuyer,
      firstBuyer,
      buyerUser,
      checkAllSaleInvoiceVehiclesInApprovedDeal,
      showWarning,
    ])

    const handleGenerateSaleInvoices = useCallback(
      async (data: SaleInvoiceSubmitData) => {
        if (pendingSaleRows.length === 0 || !firstBuyer || !buyerUser?.email) {
          showError('No Pending Sale vehicles with a valid buyer selected')
          return
        }
        const buyerName = firstBuyer.name
        const nameParts = buyerName.split(' ')
        const firstName = nameParts[0] || buyerName
        const lastName = nameParts.slice(1).join(' ') || ''
        const leadId = firstBuyer.id
        const leadName = buyerName
        const listPricesByVehicleId: Record<string, number> = Object.fromEntries(
          Object.entries(data.pdfPricingByVehicleId || {}).map(([vehicleId, values]) => [vehicleId, values.listPrice || 0])
        )
        const pdfShippingByVehicleId: Record<string, number> = Object.fromEntries(
          Object.entries(data.pdfPricingByVehicleId || {}).map(([vehicleId, values]) => [vehicleId, values.shippingPrice || 0])
        )
        const saleInvoicePdfOptions = {
          clientInfo: invoiceClientInfoFromRecipient(data.recipient),
        }

        try {
          setIsGeneratingSaleInvoice(true)
          setSaleInvoiceDialogOpen(false)
          const errors: string[] = []
          const purchaseOrderResponses: Array<{
            url: string
            documentId: string
            invoiceId: string
            vehicleId: string
          }> = []

          if (data.mode === 'invoice_pdf') {
            try {
              const pdfVehicles = pendingSaleRows.filter(v => data.allVehicleIds.includes(v.id))
              const shippingByVehicle = Object.fromEntries(
                data.allVehicleIds.map((id) => [id, pdfShippingByVehicleId[id] ?? (data.shippingPrices?.[id] || 0)])
              )
              const invoiceNumber = await generateInvoiceNumber()
              const companyInfo = getInvoiceCompanyInfo()
              if (!companyInfo) {
                throw new Error(
                  'Wire information not found. Please configure wire information in Invoice Management.'
                )
              }
              const pdfDoc = generateSaleInvoicePDF(
                pdfVehicles,
                true,
                invoiceNumber,
                companyInfo,
                shippingByVehicle,
                listPricesByVehicleId,
                data.hideVins,
                data.includeDocFee,
                data.extraLineItems,
                saleInvoicePdfOptions
              )
              if (!pdfDoc) throw new Error('Failed to generate PDF')
              const pdfBlob = pdfDoc.output('blob')
              const fileName = `invoice-${invoiceNumber}.pdf`
              const storagePath = `invoices/${leadId}/${fileName}`
              const storageRef = ref(storage, storagePath)
              await uploadBytes(storageRef, pdfBlob)
              const pdfUrl = await getDownloadURL(storageRef)
              await pandaApi.admin.savePdfInvoice(
                data.allVehicleIds,
                inventoryType as 'master_inventory' | 'shield_inventory',
                pdfUrl,
                {
                  email: data.recipient.email,
                  first_name: data.recipient.first_name,
                  last_name: data.recipient.last_name,
                },
                undefined,
                leadId,
                leadName,
                invoiceNumber,
                true,
                {
                  email: data.recipient.email,
                  first_name: data.recipient.first_name,
                  last_name: data.recipient.last_name,
                },
                shippingByVehicle
              )
              showSuccess('PDF invoice generated successfully')
            } catch (error) {
              console.error('Error generating PDF-only invoice:', error)
              showError('Failed to generate PDF invoice. Please try again.')
            } finally {
              setIsGeneratingSaleInvoice(false)
            }
            return
          }

          if (data.mode === 'down_payment_pdf') {
            try {
              const pdfVehicles = pendingSaleRows.filter(v => data.allVehicleIds.includes(v.id))
              const invoiceNumber = await generateInvoiceNumber()
              const companyInfo = getInvoiceCompanyInfo()
              if (!companyInfo) {
                throw new Error(
                  'Wire information not found. Please configure wire information in Invoice Management.'
                )
              }
              const pdfDoc = generateDownPaymentPDF(
                pdfVehicles,
                data.downPaymentAmount,
                true,
                invoiceNumber,
                companyInfo,
                data.hideVins,
                saleInvoicePdfOptions
              )
              if (!pdfDoc) throw new Error('Failed to generate PDF')
              const pdfBlob = pdfDoc.output('blob')
              const fileName = `down-payment-${invoiceNumber}.pdf`
              const storagePath = `invoices/${leadId}/${fileName}`
              const storageRef = ref(storage, storagePath)
              await uploadBytes(storageRef, pdfBlob)
              const pdfUrl = await getDownloadURL(storageRef)
              await pandaApi.admin.savePdfInvoice(
                data.allVehicleIds,
                inventoryType as 'master_inventory' | 'shield_inventory',
                pdfUrl,
                {
                  email: data.recipient.email,
                  first_name: data.recipient.first_name,
                  last_name: data.recipient.last_name,
                },
                undefined,
                leadId,
                leadName,
                invoiceNumber,
                true,
                {
                  email: data.recipient.email,
                  first_name: data.recipient.first_name,
                  last_name: data.recipient.last_name,
                },
                {}
              )
              showSuccess('Down payment PDF generated successfully')
            } catch (error) {
              console.error('Error generating down payment PDF:', error)
              showError('Failed to generate down payment PDF. Please try again.')
            } finally {
              setIsGeneratingSaleInvoice(false)
            }
            return
          }

          if (data.separateCustomerInvoices) {
            for (const vehicleId of data.allVehicleIds) {
              const vehicle = pendingSaleRows.find(v => v.id === vehicleId)
              if (!vehicle) continue
              try {
                const vehicleAssignment = data.bankAssignments.find(ba => ba.vehicleIds.includes(vehicleId))
                const bankProfileId = vehicleAssignment?.bankProfile.id
                const isCashDeal = data.cashDealVehicleIds.includes(vehicleId)
                const shippingPrice = data.shippingPrices?.[vehicleId] || 0
                const vehicleShippingPrices = { [vehicleId]: shippingPrice }
                const response = await pandaApi.admin.generateInvoice(
                  [vehicleId],
                  inventoryType,
                  data.recipient,
                  RETAIL_ORDER_TEMPLATE_NAME,
                  bankProfileId,
                  leadId,
                  leadName,
                  isCashDeal,
                  vehicleShippingPrices,
                  data.extraLineItems
                )
                purchaseOrderResponses.push({
                  url: response.url,
                  documentId: response.documentId,
                  invoiceId: response.invoiceId,
                  vehicleId,
                })
                const purchaseOrderName = `Retail Order - ${buyerName} - ${vehicle.year} ${vehicle.make} ${vehicle.model}`
                try {
                  await saveVehicleInvoice(
                    inventoryType as 'master_inventory' | 'shield_inventory',
                    vehicleId,
                    {
                      url: response.url,
                      documentId: response.documentId,
                      name: purchaseOrderName,
                      clientName: buyerName,
                      templateName: RETAIL_ORDER_TEMPLATE_NAME,
                    }
                  )
                } catch (saveError) {
                  console.error(`Error saving retail order to vehicle ${vehicleId}:`, saveError)
                  errors.push(`Failed to save retail order to vehicle ${vehicleId}`)
                }
              } catch (error) {
                console.error(`Error generating retail order for vehicle ${vehicleId}:`, error)
                errors.push(`Failed to generate retail order for vehicle ${vehicleId}`)
              }
            }
          } else {
            try {
              const firstBankProfileId =
                data.bankAssignments.length > 0 ? data.bankAssignments[0].bankProfile.id : undefined
              const orderShippingPrices: Record<string, number> = {}
              data.allVehicleIds.forEach(id => {
                orderShippingPrices[id] = data.shippingPrices?.[id] || 0
              })
              const response = await pandaApi.admin.generateInvoice(
                data.allVehicleIds,
                inventoryType,
                data.recipient,
                RETAIL_ORDER_TEMPLATE_NAME,
                firstBankProfileId,
                leadId,
                leadName,
                data.cashDealVehicleIds.length === data.allVehicleIds.length,
                orderShippingPrices,
                data.extraLineItems
              )
              purchaseOrderResponses.push({
                url: response.url,
                documentId: response.documentId,
                invoiceId: response.invoiceId,
                vehicleId: '',
              })
              const purchaseOrderName = `Retail Order - ${buyerName}`
              try {
                await Promise.all(
                  pendingSaleRows.map(vehicle =>
                    saveVehicleInvoice(
                      inventoryType as 'master_inventory' | 'shield_inventory',
                      vehicle.id,
                      {
                        url: response.url,
                        documentId: response.documentId,
                        name: purchaseOrderName,
                        clientName: buyerName,
                        templateName: RETAIL_ORDER_TEMPLATE_NAME,
                      }
                    )
                  )
                )
              } catch (saveError) {
                console.error('Error saving retail order to vehicles:', saveError)
                errors.push('Failed to save retail order to vehicles')
              }
            } catch (error) {
              console.error('Error generating retail order:', error)
              errors.push('Failed to generate retail order')
            }
          }

          if (data.cashDealVehicleIds.length > 0) {
            try {
              const cashDealVehicles = pendingSaleRows.filter(v =>
                data.cashDealVehicleIds.includes(v.id)
              )
              const cashDealShippingByVehicle = Object.fromEntries(
                data.cashDealVehicleIds.map(id => [id, data.shippingPrices?.[id] || 0])
              )
              const cashDealListPricesByVehicle = Object.fromEntries(
                data.cashDealVehicleIds.map(id => [id, listPricesByVehicleId[id]])
              )
              const invoiceNumber = await generateInvoiceNumber()
              const companyInfo = getInvoiceCompanyInfo()
              if (!companyInfo) {
                throw new Error(
                  'Wire information not found. Please configure wire information in Invoice Management.'
                )
              }
              const pdfDoc = generateSaleInvoicePDF(
                cashDealVehicles,
                true,
                invoiceNumber,
                companyInfo,
                cashDealShippingByVehicle,
                cashDealListPricesByVehicle,
                data.hideVins,
                true,
                data.extraLineItems,
                saleInvoicePdfOptions
              )
              if (!pdfDoc) throw new Error('Failed to generate PDF')
              const pdfBlob = pdfDoc.output('blob')
              const fileName = `invoice-${invoiceNumber}.pdf`
              const storagePath = `invoices/${leadId}/${fileName}`
              const storageRef = ref(storage, storagePath)
              await uploadBytes(storageRef, pdfBlob)
              const pdfUrl = await getDownloadURL(storageRef)
              await pandaApi.admin.savePdfInvoice(
                data.cashDealVehicleIds,
                inventoryType as 'master_inventory' | 'shield_inventory',
                pdfUrl,
                { email: buyerUser.email!, first_name: firstName, last_name: lastName },
                undefined,
                leadId,
                leadName,
                invoiceNumber,
                true,
                {
                  email: buyerUser.email!,
                  first_name: firstName,
                  last_name: lastName,
                },
                cashDealShippingByVehicle
              )
            } catch (error) {
              console.error('Error generating PDF invoice for cash deals:', error)
              errors.push('Failed to generate PDF invoice for cash deals')
            }
          }

          if (data.bankAssignments.length > 0) {
            for (const assignment of data.bankAssignments) {
              try {
                const { vehicleIds, bankProfile } = assignment
                const bankVehicles = pendingSaleRows.filter(v => vehicleIds.includes(v.id))
                const bankShippingByVehicle = Object.fromEntries(
                  vehicleIds.map(id => [id, data.shippingPrices?.[id] || 0])
                )
                const bankListPricesByVehicle = Object.fromEntries(
                  vehicleIds.map(id => [id, listPricesByVehicleId[id]])
                )
                const invoiceNumber = await generateInvoiceNumber()
                const companyInfo = getInvoiceCompanyInfo()
                if (!companyInfo) {
                  throw new Error(
                    'Wire information not found. Please configure wire information in Invoice Management.'
                  )
                }
                const pdfDoc = generateSaleInvoicePDF(
                  bankVehicles,
                  true,
                  invoiceNumber,
                  companyInfo,
                  bankShippingByVehicle,
                  bankListPricesByVehicle,
                  data.hideVins,
                  true,
                  data.extraLineItems,
                  saleInvoicePdfOptions
                )
                if (!pdfDoc) throw new Error('Failed to generate PDF')
                const pdfBlob = pdfDoc.output('blob')
                const fileName = `invoice-${invoiceNumber}.pdf`
                const storagePath = `invoices/${leadId}/${fileName}`
                const storageRef = ref(storage, storagePath)
                await uploadBytes(storageRef, pdfBlob)
                const pdfUrl = await getDownloadURL(storageRef)
                const bankEmail =
                  bankProfile.bankContacts?.length
                    ? bankProfile.bankContacts[0].email
                    : ''
                const bankContactName =
                  bankProfile.bankContacts?.length
                    ? bankProfile.bankContacts[0].name
                    : bankProfile.bankName
                const bankNameParts = bankContactName.split(' ')
                const bankFirstName = bankNameParts[0] || bankProfile.bankName
                const bankLastName = bankNameParts.slice(1).join(' ') || ''
                await pandaApi.admin.savePdfInvoice(
                  vehicleIds,
                  inventoryType as 'master_inventory' | 'shield_inventory',
                  pdfUrl,
                  {
                    email: bankEmail,
                    first_name: bankFirstName,
                    last_name: bankLastName,
                  },
                  bankProfile.id,
                  leadId,
                  leadName,
                  invoiceNumber,
                  false,
                  {
                    email: buyerUser.email!,
                    first_name: firstName,
                    last_name: lastName,
                  },
                  bankShippingByVehicle
                )
              } catch (error) {
                console.error(`Error generating PDF invoice for ${assignment.bankProfile.bankName}:`, error)
                errors.push(`Failed to generate PDF invoice for ${assignment.bankProfile.bankName}`)
              }
            }
          }

          if (data.generateOdometerStatements) {
            for (const vehicleId of data.allVehicleIds) {
              try {
                const vehicle = pendingSaleRows.find(v => v.id === vehicleId)
                if (!vehicle) continue
                await pandaApi.admin.generateOdometerStatement(
                  vehicleId,
                  inventoryType,
                  {
                    email: data.recipient.email,
                    first_name: data.recipient.first_name,
                    last_name: data.recipient.last_name,
                  },
                  leadId,
                  leadName
                )
              } catch (error) {
                console.error(`Error generating odometer statement for vehicle ${vehicleId}:`, error)
                errors.push(`Error generating odometer statement for vehicle ${vehicleId}`)
              }
            }
          }

          if (purchaseOrderResponses.length > 0) {
            const messages: string[] = [
              data.separateCustomerInvoices
                ? `${purchaseOrderResponses.length} retail order(s) generated successfully`
                : 'Purchase order generated successfully',
            ]
            if (data.cashDealVehicleIds.length > 0) {
              messages.push(`PDF invoice generated for ${data.cashDealVehicleIds.length} cash deal(s)`)
            }
            if (data.bankAssignments.length > 0) {
              messages.push(`PDF invoice(s) generated for ${data.bankAssignments.length} bank(s)`)
            }
            if (data.generateOdometerStatements) {
              messages.push(`Odometer statement(s) generated for ${data.allVehicleIds.length} vehicle(s)`)
            }
            showSuccess(messages.join('. '))
          }
          if (errors.length > 0) {
            showWarning(`Some errors occurred: ${errors.join(', ')}`)
          }
          if (purchaseOrderResponses.length === 0 && errors.length > 0) {
            showError('Failed to generate purchase order. Please try again.')
          }
        } catch (error) {
          console.error('Error generating sale invoices:', error)
          showError('Failed to generate sale invoices. Please try again.')
        } finally {
          setIsGeneratingSaleInvoice(false)
        }
      },
      [
        pendingSaleRows,
        firstBuyer,
        buyerUser,
        inventoryType,
        generateInvoiceNumber,
        getInvoiceCompanyInfo,
        showSuccess,
        showError,
        showWarning,
      ]
    )

    const handleCreateDealApprovalForSaleInvoice = useCallback(() => {
      if (pendingSaleRows.length === 0) {
        showError('No vehicles to create deal approval for')
        return
      }
      const vehicleIds = pendingSaleRows.map(v => v.id)
      const vehicle_summaries = pendingSaleRows.map(v => ({
        stock_number: v.stock_number ?? '',
        vin: v.vin ?? '',
      }))
      createDealApproval(
        {
          vehicle_ids: vehicleIds,
          vehicle_summaries,
          notes: `Deal approval request created from Inventory for buyer: ${firstBuyer?.name ?? 'Unknown'}`,
        },
        {
          onSuccess: () => {
            showSuccess(`Deal approval request created for ${vehicleIds.length} vehicle(s)`)
            setSaleInvoiceDealApprovalDialogOpen(false)
          },
          onError: (error: Error) => {
            console.error('Error creating deal approval:', error)
            showError('Failed to create deal approval request')
          },
        }
      )
    }, [pendingSaleRows, firstBuyer, createDealApproval, showSuccess, showError])

    // Handler for generating invoice with selected client or manual entry
    const handleGenerateInvoice = useCallback(async (
      clientOrSigner: PrivateUserInfoType | SignerInfo,
      bankProfileAssignments?: VehicleBankProfileAssignment[]
    ) => {
      try {
        setLoadingMessage('Generating invoice...')
        setLoading(true)
        setInvoiceDialogOpen(false)

        const vehicleIds = selectedRows.map(row => row.id)
        
        // Extract signer information from either client or manual entry
        const signerInfo: SignerInfo = {
          email: clientOrSigner.email || '',
          first_name: clientOrSigner.first_name || '',
          last_name: clientOrSigner.last_name || '',
        }
        if ('company' in clientOrSigner && clientOrSigner.company) signerInfo.company = clientOrSigner.company
        if ('street_address' in clientOrSigner && clientOrSigner.street_address) signerInfo.street_address = clientOrSigner.street_address
        if ('city' in clientOrSigner && clientOrSigner.city) signerInfo.city = clientOrSigner.city
        if ('state' in clientOrSigner && clientOrSigner.state) signerInfo.state = clientOrSigner.state
        if ('postal_code' in clientOrSigner && clientOrSigner.postal_code) signerInfo.postal_code = clientOrSigner.postal_code
        
        const response = await pandaApi.admin.generateInvoice(
          vehicleIds,
          inventoryType,
          signerInfo,
          'Purchase Order'
        )

        // Save invoice to all selected vehicles
        const clientName = `${signerInfo.first_name || ''} ${signerInfo.last_name || ''}`.trim() || signerInfo.email || 'Unknown Client'
        const invoiceName = `Purchase Order - ${clientName}`
        
        try {
          await Promise.all(
            selectedRows.map(vehicle =>
              saveVehicleInvoice(
                inventoryType as 'master_inventory' | 'shield_inventory',
                vehicle.id,
                {
                  url: response.url,
                  documentId: response.documentId,
                  name: invoiceName,
                  clientName: clientName,
                  templateName: 'Purchase Order',
                }
              )
            )
          )
        } catch (saveError) {
          console.error('Error saving purchase order to vehicles:', saveError)
          // Don't fail the whole operation if saving fails
          showWarning('Purchase order generated but failed to save to some vehicles')
        }

        // Generate payoff authorization for vehicles with liens
        const vehiclesWithLiens = selectedRows.filter(vehicle => vehicle.has_lien === true)
        if (vehiclesWithLiens.length > 0 && bankProfileAssignments) {
          try {
            setLoadingMessage('Generating payoff authorization...')
            
            // Group vehicles by unique bank profile
            // Each unique bank gets its own separate payoff authorization
            const vehiclesByBank = new Map<string, string[]>()
            const vehiclesWithoutBank: string[] = []
            
            bankProfileAssignments.forEach(assignment => {
              if (assignment.bankProfile) {
                const bankId = assignment.bankProfile.id
                if (!vehiclesByBank.has(bankId)) {
                  vehiclesByBank.set(bankId, [])
                }
                vehiclesByBank.get(bankId)!.push(assignment.vehicleId)
              } else {
                // Track vehicles without a bank selected
                vehiclesWithoutBank.push(assignment.vehicleId)
              }
            })
        
            // Generate payoff authorization for each unique bank
            // Each bank gets its own separate payoff authorization document
            const payoffPromises: Promise<any>[] = []
            
            // Generate one payoff authorization per unique bank profile
            vehiclesByBank.forEach((vehicleIdsForBank, bankId) => {
              payoffPromises.push(
                pandaApi.admin.generatePayoffAuthorization(
                  vehicleIdsForBank,
                  inventoryType,
                  signerInfo,
                  bankId,
                  response.invoiceId // Pass purchase order ID
                )
              )
            })
            
            // If there are vehicles with liens but no bank selected, create one payoff authorization for them
            if (vehiclesWithoutBank.length > 0) {
              payoffPromises.push(
                pandaApi.admin.generatePayoffAuthorization(
                  vehiclesWithoutBank,
                  inventoryType,
                  signerInfo,
                  undefined,
                  response.invoiceId // Pass purchase order ID
                )
              )
            }

            const payoffResponses = await Promise.all(payoffPromises)
            
            showSuccess(
              `Purchase order and ${payoffResponses.length} payoff authorization(s) generated successfully!`
            )
          } catch (payoffError) {
            console.error('Error generating payoff authorization:', payoffError)
            showWarning('Purchase order generated but failed to generate payoff authorization(s)')
          }
        } else {
          showSuccess(`Purchase order generated successfully! ${response.vehicleCount} vehicle(s) included.`)
        }
      } catch (error) {
        console.error('Error generating PO:', error)
        showError('Failed to generate PO. Please try again.')
      } finally {
        setLoading(false)
      }
    }, [selectedRows, inventoryType, showSnackbar])

    return (
      <>
        <LoadingDialog open={isLoading} message={loadingMessage || 'Loading permissions...'} />
        <DeleteConfirmationDialog
          open={showDeleteConfirm}
          vehicles={selectedRows}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
        <PendingSaleNotPurchasedDialog
          open={pendingSaleNotPurchasedOpen}
          onClose={() => {
            setPendingSaleNotPurchasedOpen(false)
            setPendingSaleNotPurchasedVehicles([])
          }}
          vehicles={pendingSaleNotPurchasedVehicles}
          onUnlocked={() => {
            setPendingSaleNotPurchasedOpen(false)
            setPendingSaleNotPurchasedVehicles([])
            beginPendingSaleFlow()
          }}
          onNotify={handleNotifyPendingSaleNotPurchased}
        />
        <AddToListDialog
          open={showAddToList}
          onClose={() => setShowAddToList(false)}
          onSubmit={handleAddToList}
          lists={lists || []}
          loading={isLoading || listsLoading}
          users={users}
        />
        {/* Vehicle Selection Dialog - only shown when not on the sold step */}
        {vehicleSelectionOpen && !soldPopupOpen && (
          <VehicleSaleSelectionDialog
            open={true}
            onClose={() => {
              setVehicleSelectionOpen(false)
              setRemainingVehicles([]) // Reset if closed manually
            }}
            vehicles={remainingVehicles}
            onConfirm={handleVehicleSelectionConfirm}
            dialogTitle={
              saleActionType === 'pending_sale'
                ? 'Select Vehicles to Mark as Pending Sale'
                : 'Select Vehicles to Mark as Sold'
            }
            description={
              saleActionType === 'pending_sale'
                ? 'Select which vehicles you want to mark as pending sale with the same client and sales representative.'
                : 'Select which vehicles you want to mark as sold with the same client and sales representative.'
            }
          />
        )}

        {/* SoldDialog - only shown when on the sold step */}
        {soldPopupOpen && (
          <SoldDialog
            open={true}
            onClose={() => {
              setSoldPopupOpen(false)
              // Don't close the selection dialog, just go back to it
              setSelectedVehiclesToSell([])
            }}
            onSubmit={handleSoldSubmit}
            itemName={
              selectedVehiclesToSell.length === 1
                ? selectedVehiclesToSell[0].stock_number
                : `${selectedVehiclesToSell.length} vehicles`
            }
            actionType={
              saleActionType === 'pending_sale' ? 'pending_sale' : canMarkSold ? 'sold' : 'sold_elsewhere'
            }
            currentLocation={
              selectedVehiclesToSell.length === 1 ? selectedVehiclesToSell[0].location || undefined : undefined
            }
            initialData={
              selectedVehiclesToSell.length === 1
                ? saleActionType === 'pending_sale'
                  ? {
                      soldToUser: selectedVehiclesToSell[0].selling_to_user || undefined,
                      soldByUser: selectedVehiclesToSell[0].sold_by_user || undefined,
                      soldLocation: selectedVehiclesToSell[0].sold_location || undefined,
                    }
                  : {
                      funded: Boolean(selectedVehiclesToSell[0].funded) || Boolean(selectedVehiclesToSell[0].funded_date),
                      fundedDate: selectedVehiclesToSell[0].funded_date ?? null,
                    }
                : undefined
            }
          />
        )}

        {/* Add the Manage Photos Dialog */}
        {selectedRows.length === 1 && (
          <ManagePhotosDialog
            collectionName={inventoryType}
            open={managePhotosOpen}
            onClose={() => setManagePhotosOpen(false)}
            vehicle={selectedRows[0]}
          />
        )}

        {/* Invoice Dialog */}
        <InvoiceDialog
          open={invoiceDialogOpen}
          onClose={() => setInvoiceDialogOpen(false)}
          onSubmit={handleGenerateInvoice}
          loading={isLoading}
          vehicles={selectedRows}
        />

        {/* Sale Invoice (Retail Order) - only for Pending Sale with buyer */}
        <LoadingDialog open={isGeneratingSaleInvoice} message='Generating Retail Orders...' />
        <DealApprovalWarningDialog
          open={saleInvoiceDealApprovalDialogOpen}
          onClose={() => setSaleInvoiceDealApprovalDialogOpen(false)}
          onCreateDeal={handleCreateDealApprovalForSaleInvoice}
          onContinueAnyway={() => {
            setSaleInvoiceDealApprovalDialogOpen(false)
            setSaleInvoiceDialogOpen(true)
          }}
          vehicles={pendingSaleRows}
          loading={isCreatingDeal}
          inventoryType={inventoryType}
          existingDeal={findExistingDealForSaleInvoice()}
        />
        <SaleInvoiceDialog
          open={saleInvoiceDialogOpen}
          onClose={() => setSaleInvoiceDialogOpen(false)}
          vehicles={pendingSaleRows}
          onSubmit={handleGenerateSaleInvoices}
          loading={isGeneratingSaleInvoice}
          initialRecipient={saleInvoiceInitialRecipient}
        />

        {/* Deal Approval Confirmation Dialog */}
        <DealApprovalConfirmationDialog
          open={dealApprovalDialogOpen}
          vehicles={selectedRows}
          onConfirm={handleConfirmDealApproval}
          onCancel={() => setDealApprovalDialogOpen(false)}
          loading={isCreatingDeal}
          inventoryType={inventoryType}
          canViewPurchasePrice={canViewPurchasePrice}
          canViewProfit={canViewProfit}
        />

        <BulkPriceUpdateDialog
          open={bulkPriceUpdateOpen}
          onClose={() => setBulkPriceUpdateOpen(false)}
          vehicles={selectedRows}
          onSubmit={handleBulkPriceUpdate}
          loading={isLoading}
        />

        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant='h6' color='text.primary'>
            Actions
          </Typography>
          <Divider />

          <Tooltip title={getSoldButtonTooltip()}>
            <span style={tooltipWrapStyle}>
              <Button
                startIcon={<ArchiveIcon />}
                variant='contained'
                onClick={handleMarkSold}
                disabled={!selectedRows.length || isLoading || !canMarkSold}
                sx={buttonStyles}
              >
                Mark Selected as Sold
              </Button>
            </span>
          </Tooltip>
          <Tooltip title='Mark all selected vehicles as pending sale (in the grid view)'>
            <span style={tooltipWrapStyle}>
              <Button
                startIcon={<PendingActionsIcon />}
                variant='contained'
                onClick={handleMarkPendingSale}
                disabled={!selectedRows.length || isLoading || !canMarkSold}
                sx={buttonStyles}
              >
                Mark Selected as Pending Sale
              </Button>
            </span>
          </Tooltip>
          {userInfo?.isOwner && (
            <Tooltip title={fundedEligibleRows.length === 0 && selectedRows.length > 0 ? 'Select only vehicles with "Purchased for inventory" to mark as funded' : 'Mark selected vehicles (purchased for inventory) as funded (sets funded date)'}>
              <span style={tooltipWrapStyle}>
                <Button
                  startIcon={<CheckCircleIcon />}
                  variant='outlined'
                  onClick={handleMarkFunded}
                  disabled={fundedEligibleRows.length === 0 || isLoading || !canModifyInventory}
                  sx={buttonSizing}
                >
                  Mark Selected as Funded
                </Button>
              </span>
            </Tooltip>
          )}
          {canBulkUpdatePrice && (
            <Button
              startIcon={<AttachMoneyIcon />}
              variant='contained'
              onClick={handleOpenBulkPriceUpdate}
              disabled={!selectedRows.length || isLoading}
              sx={buttonStyles}
            >
              Update Price
            </Button>
          )}
          <Button
            startIcon={<PlaylistAddIcon />}
            variant='contained'
            onClick={() => setShowAddToList(true)}
            disabled={!selectedRows.length || isLoading || !canCreateInventory}
            sx={buttonStyles}
          >
            Add Selected to List
          </Button>
          <Button
            startIcon={<DescriptionIcon />}
            variant='contained'
            onClick={handleOpenInvoiceDialog}
            disabled={!selectedRows.length || isLoading}
            sx={buttonStyles}
          >
            Generate Purchase Order
          </Button>
          <Tooltip
            title={
              !canGenerateSaleInvoice && selectedRows.length > 0
                ? 'Select only vehicles with status "Pending Sale" that have the same buyer (from Mark as Pending Sale)'
                : ''
            }
          >
            <span style={tooltipWrapStyle}>
              <Button
                startIcon={<DescriptionIcon />}
                variant='outlined'
                onClick={handleOpenSaleInvoiceDialog}
                disabled={!canGenerateSaleInvoice || isLoading || isLoadingDeals}
                sx={buttonSizing}
              >
                Generate Sale Invoice
              </Button>
            </span>
          </Tooltip>
          <Button
            startIcon={<CheckCircleIcon />}
            variant='contained'
            onClick={handleOpenDealApprovalDialog}
            disabled={!selectedRows.length || isLoading || isCreatingDeal}
            sx={buttonStyles}
          >
            Get Approved
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            variant='contained'
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!selectedRows.length || isLoading || !canDeleteInventory}
            sx={deleteButtonStyles}
          >
            Delete Selected
          </Button>
          <Divider />
          <Button
            startIcon={<FileDownloadIcon />}
            variant='contained'
            onClick={handleDownloadCsv}
            disabled={!selectedRows.length || isLoading}
            sx={buttonStyles}
          >
            Download CSV
          </Button>
          <Button
            startIcon={<PictureAsPdfIcon />}
            variant='contained'
            onClick={handleDownloadPdf}
            disabled={!selectedRows.length || isLoading}
            sx={buttonStyles}
          >
            Download PDF
          </Button>
          <Tooltip
            title={
              pdfColumnLimitExceeded
                ? 'Reduce visible columns to 15 or fewer; too many columns don\'t fit on the PDF properly.'
                : ''
            }
          >
            <span style={tooltipWrapStyle}>
              <Button
                startIcon={<PictureAsPdfIcon />}
                endIcon={
                  <Chip
                    label='Beta'
                    size='small'
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      color: 'inherit',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                }
                onClick={handleDownloadCustomPdf}
                disabled={!selectedRows.length || isLoading || pdfColumnLimitExceeded}
                sx={{ ...customPdfButtonStyles, minWidth: '100%' }}
              >
                Download custom PDF
              </Button>
            </span>
          </Tooltip>
          <Button
            startIcon={<GetAppIcon />}
            variant='outlined'
            onClick={handleDownloadCSVTemplate}
            disabled={isLoading}
            sx={buttonSizing}
          >
            CSV Template
          </Button>
          <Button
            component='label'
            startIcon={<UploadFileIcon />}
            variant='outlined'
            disabled={isLoading || !canCreateInventory}
            sx={buttonSizing}
          >
            Upload CSV
            <input
              type='file'
              hidden
              onChange={handleCsvUpload}
              disabled={isLoading || !canCreateInventory}
              accept='.csv'
            />
          </Button>
        </Stack>
      </>
    )
  },
)

ActionsPanel.displayName = 'ActionsPanel'

export { ActionsPanel }
